// Wire identifiers turned into words.
//
// **This is the one place the client is allowed to hold prose**, and it
// holds only prose: nothing structural is decided here, so a name missing
// from the table below still gets a working control with a worse label.
// That is the whole test for what may live in this file — if a type
// absent from it stopped working rather than reading awkwardly, the fact
// belongs on the wire instead.
//
// It is deliberately not `model/schema.ts`. A schema says what a shape
// *is*; this says what to call it, and the two drift apart the moment the
// manifest carries the per-type overlay §1.5 describes — at which point
// everything here becomes a fallback for a type whose prose has not been
// written yet, rather than something to delete.

/**
 * A humanised label for a property, used until the manifest carries
 * prose.
 *
 * `linkId` becomes "Link id", `navVisible` becomes "Nav visible". A poor
 * label beats a raw identifier, and beats blocking the whole mechanism
 * on an overlay file that does not exist yet.
 */
export function humanise(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/**
 * A noun with the article it takes: "a photo", "an image or file".
 *
 * Read off the first letter, which is right for every word a type's prose
 * has used so far; "an hour" and "a user" would read wrong, and are not
 * words a type is called.
 */
export function aOrAn(word: string): string {
  return /^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`
}

/**
 * An event is named for what happened — `presentation_set`, `retitled` —
 * and a control is labelled for what it will do. English will not derive
 * one from the other by rule: `retitled` drops a letter, `edited` drops
 * two, `added` drops three, and a suffix heuristic confident enough to
 * get those right produces "Adde" and "Reordere" on the next one.
 *
 * So the verbs are listed and the fallback is honest: an intent whose
 * last word is not here shows its own humanised name rather than a
 * mangled guess. A type missing from this table still gets a working
 * form and a working placement control.
 */
const IMPERATIVE: Record<string, string> = {
  added: 'Add',
  bound: 'Bind',
  changed: 'Change',
  cleared: 'Clear',
  configured: 'Configure',
  described: 'Describe',
  edited: 'Edit',
  placed: 'Place',
  released: 'Release',
  removed: 'Remove',
  renamed: 'Rename',
  reordered: 'Reorder',
  replaced: 'Replace',
  retitled: 'Retitle',
  set: 'Set',
  updated: 'Update',
}

/**
 * The button's words: the verb moved to the front of what it acts on.
 *
 * `container.presentation_set` reads "Set presentation" rather than
 * "Presentation set", which is the same reading order every hand-written
 * button in this client already used.
 *
 * **It is also the only thing that says what a placement action does.**
 * The markers on a payload say that an intent names one existing
 * placement; they cannot say whether naming it removes it or makes it the
 * home page, because those two have the same shape. So a control that
 * offers a per-placement action labels it from here — "Remove block",
 * "Set home" — and an author reads the intent rather than this client's
 * guess at what the type meant.
 */
export function actionLabel(intent: string): string {
  const { verb, subject } = nameParts(intent)
  if (!verb) return humanise(subject)
  return subject ? `${verb} ${humanise(subject).toLowerCase()}` : verb
}

/** The section's words: what the intent acts on, or the action itself when it names nothing. */
export function subjectLabel(intent: string): string {
  const { subject } = nameParts(intent)
  return subject ? humanise(subject) : actionLabel(intent)
}

function nameParts(intent: string): { verb?: string; subject: string } {
  const local = intent.slice(intent.lastIndexOf('.') + 1)
  const words = local.split('_')
  const verb = IMPERATIVE[words[words.length - 1] ?? '']
  return verb ? { verb, subject: words.slice(0, -1).join('_') } : { subject: local }
}

/**
 * What a lagging listing says about itself, in one sentence and in one
 * place.
 *
 * Six screens draw a listing served from the tenant index, and each had
 * written its own explanation of why a component made a moment ago is
 * not in it. All six were accurate and all six described the
 * *mechanism* — "served from the projection", "forward-only", "built by
 * projecting" — which is a sentence an author cannot act on and cannot
 * check. What they can act on is the effect and the remedy: it will
 * appear, wait a moment.
 *
 * It lives here because it is prose and nothing else: the lag is real
 * whether or not this string exists, and a screen that dropped it would
 * read worse rather than work differently.
 */
export const LISTING_LAG = 'Something made in the last few seconds may not be listed yet.'

/**
 * The same fact where a listing is empty rather than merely behind.
 *
 * A separate sentence because an author reading an empty screen is
 * asking a different question — "is it broken, or is it just new?" —
 * and "wait a moment" alone does not answer it.
 */
export const LISTING_EMPTY = 'Nothing has been made here yet. Anything made in the last few seconds may still be on its way.'
